import { computed } from 'vue';
import { useRouter } from 'vue-router';
import { useApolloClient, useSubscription } from '@vue/apollo-composable';
import { SessionLoggedOutDocument } from '@hive/graphql';
import { useAuthStore } from '@/stores/auth';

/**
 * Global logout subscription composable.
 * Listens for session logout events and redirects to the local login page.
 * Uses sessionHash to sync logout across browser tabs sharing the same session.
 * Should be initialized once in App.vue.
 */
export const useLogoutSubscription = () => {
  const authStore = useAuthStore();
  const { client } = useApolloClient();
  const router = useRouter();

  const sessionHash = computed(() => authStore.user?.sessionHash);

  const { onResult, onError } = useSubscription(
    SessionLoggedOutDocument,
    () => ({
      variables: { sessionHash: sessionHash.value ?? '' },
      // Mute on tabs that initiated the logout themselves — useLogout handles
      // the teardown there. We still want the subscription active on OTHER
      // tabs sharing the same session.
      enabled: !!sessionHash.value && !authStore.isLoggingOut,
    }),
  );

  onResult((data) => {
    if (data?.sessionLoggedOut) {
      void handleLogoutEvent();
    }
  });

  onError((error) => {
    console.error('Logout subscription error:', error);
  });

  const handleLogoutEvent = async () => {
    // Already navigated to Login (most likely because useLogout on this tab
    // beat the WS event) — nothing to do.
    if (router.currentRoute.value.name === 'Login') {
      return;
    }

    try {
      await client.clearStore();
    } catch {
      // clearStore aborts in-flight queries; ignore — we're tearing down.
    }

    try {
      // Do NOT $reset (which would null user.value and crash MainLayout
      // mid-transition). The server has invalidated this session, so /me now
      // returns Guest, and the auth middleware accepts Guest on /login.
      await authStore.fetchMe();
    } catch {
      // ignore
    }

    void router.replace({
      name: 'Login',
      query: { reason: 'session_ended' },
    });
  };
};
